import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, DollarSign, TrendingUp, TrendingDown, 
  Calendar, Download, Wallet, CreditCard
} from 'lucide-react';
import { paymentService } from '@/services/paymentService';
import type { Transaction } from '@/types';

const OwnerFinance: React.FC = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadFinanceData();
  }, []);

  const loadFinanceData = async () => {
    try {
      const [txns, total, today] = await Promise.all([
        paymentService.getAllTransactions(50),
        paymentService.getTotalRevenue(),
        paymentService.getTodayRevenue(),
      ]);
      setTransactions(txns);
      setTotalRevenue(total);
      setTodayRevenue(today);
    } catch (error) {
      console.error('Error loading finance data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ride_payment: 'دفع رحلة',
      wallet_deposit: 'شحن محفظة',
      wallet_withdrawal: 'سحب من محفظة',
      driver_payout: 'دفع للسائق',
      refund: 'استرداد',
      bonus: 'مكافأة',
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'مكتمل' },
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'معلق' },
      failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'فاشل' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'ملغي' },
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/owner/dashboard')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">المالية</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Revenue Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6" />
              </div>
              <TrendingUp className="w-6 h-6 text-white/70" />
            </div>
            <p className="text-white/70 text-sm mb-1">إجمالي الإيرادات</p>
            <p className="text-3xl font-bold">{totalRevenue.toLocaleString()} ج.س</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6" />
              </div>
              <TrendingUp className="w-6 h-6 text-white/70" />
            </div>
            <p className="text-white/70 text-sm mb-1">إيرادات اليوم</p>
            <p className="text-3xl font-bold">{todayRevenue.toLocaleString()} ج.س</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">المحفظة</p>
            <p className="text-xl font-bold text-primary-600">0 ج.س</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">بنكك</p>
            <p className="text-xl font-bold text-green-600">0 ج.س</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">فوري</p>
            <p className="text-xl font-bold text-yellow-600">0 ج.س</p>
          </div>
          <div className="bg-white rounded-xl p-4">
            <p className="text-gray-500 text-sm">ساداد</p>
            <p className="text-xl font-bold text-blue-600">0 ج.س</p>
          </div>
        </div>

        {/* Transactions */}
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-4 border-b flex items-center justify-between">
            <h2 className="font-bold text-gray-800">المعاملات الأخيرة</h2>
            <button className="text-primary-600 text-sm flex items-center gap-1">
              <Download className="w-4 h-4" />
              تصدير
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا يوجد معاملات</p>
            </div>
          ) : (
            <div className="divide-y">
              {transactions.map((txn) => {
                const status = getStatusBadge(txn.status);
                return (
                  <div key={txn.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        txn.type === 'ride_payment' ? 'bg-green-100 text-green-600' :
                        txn.type === 'wallet_deposit' ? 'bg-blue-100 text-blue-600' :
                        txn.type === 'wallet_withdrawal' ? 'bg-red-100 text-red-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        <Wallet className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-medium">{getTransactionTypeLabel(txn.type)}</p>
                        <p className="text-gray-500 text-sm">{txn.description}</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className={`font-bold ${
                        txn.type === 'wallet_withdrawal' || txn.type === 'driver_payout' 
                          ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {txn.type === 'wallet_withdrawal' || txn.type === 'driver_payout' ? '-' : '+'}
                        {txn.amount} ج.س
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default OwnerFinance;
